import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { connectRange, Panel } from 'react-instantsearch-dom';
import { Overlay, Popover } from 'react-bootstrap';
import moment from 'moment';
import { DateAttributesInterface } from '../../admin-dashboard-tables/interfaces';

interface Props {
  attribute: string,
  attributeObject: DateAttributesInterface,
  currentRefinement: { min: number, max: number },
  min: number,
  max: number,
  precision: number,
  numericFilters: Array<string>,
  setNumericFilters: (filters: Array<string>) => void,
  refine,
}

const DateRange: React.FC<Props> = ({
  attribute,
  attributeObject,
  numericFilters,
  setNumericFilters,
}) => {
  // const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  // const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const {
    DisplayComponent,
    startLabel,
    endLabel,
  } = attributeObject;
  const [target, setTarget] = useState<null|HTMLElement>(null);
  const ref = useRef(null);

  useEffect(() => {
    const currentDateRange = numericFilters.find((fil: string) => fil.indexOf(attribute) !== -1);
    // console.log(currentDateRange);
    if (currentDateRange) {
      const hasColon = currentDateRange.indexOf(':') !== -1;
      const justFrom = currentDateRange.indexOf('>') !== -1;
      const justTo = currentDateRange.indexOf('<') !== -1;
      if (hasColon) {
        const regex = new RegExp(`${attribute}:|\\sTO\\s`);
        const [currStart, currEnd] = currentDateRange
          .split(regex)
          .filter((el: string) => el)
          .map((time: string) => parseInt(time, 10));
        // console.log({ currStart, currEnd });
        const convStart = moment.unix(currStart).utc().format('YYYY-MM-DD');
        const convEnd = moment.unix(currEnd).utc().format('YYYY-MM-DD');
        if (convStart !== startDate) {
          // console.log({ convStart, startDate });
          setStartDate(convStart);
        }
        if (convEnd !== endDate) {
          // console.log({ convEnd, endDate });
          setEndDate(convEnd);
        }
      } else if (justFrom) {
        const [currStart] = currentDateRange
          .split(' > ')
          .filter((el: string) => el.indexOf(attribute) === -1)
          .map((time: string) => parseInt(time, 10));
        const convStart = moment.unix(currStart).utc().format('YYYY-MM-DD');
        if (convStart !== startDate) {
          // console.log({ convStart, startDate });
          setStartDate(convStart);
        }
      } else if (justTo) {
        const [currEnd] = currentDateRange
          .split(' < ')
          .filter((el: string) => el.indexOf(attribute) === -1)
          .map((time: string) => parseInt(time, 10));
        const convEnd = moment.unix(currEnd).utc().format('YYYY-MM-DD');
        if (convEnd !== endDate) {
          // console.log({ convEnd, endDate });
          setEndDate(convEnd);
        }
      }
    } else {
      if (startDate) {
        setStartDate('');
      }
      if (endDate) {
        setEndDate('');
      }
    }
  }, [attribute, numericFilters]);

  useEffect(() => {
    if (startDate && endDate) {
      // refine({
      //   min: moment(startDate).utc().format('X'),
      //   max: moment(endDate).utc().format('X'),
      // });
      setNumericFilters([
        ...numericFilters.filter((item: string) => item.indexOf(attribute) === -1),
        `${attribute}:${moment(startDate).utc().format('X')} TO ${moment(endDate).utc().format('X')}`,
      ]);
      // setNumericFilters()
    } else if (startDate) {
      setNumericFilters([
        ...numericFilters.filter((item: string) => item.indexOf(attribute) === -1),
        `${attribute} > ${moment(startDate).utc().format('X')}`,
      ]);
    } else if (endDate) {
      setNumericFilters([
        ...numericFilters.filter((item: string) => item.indexOf(attribute) === -1),
        `${attribute} < ${moment(endDate).utc().format('X')}`,
      ]);
    } else {
      setNumericFilters([
        ...numericFilters.filter((item: string) => item.indexOf(attribute) === -1),
      ]);
    }
  }, [startDate, endDate]);

  const reset = useCallback(() => {
    if (startDate) {
      setStartDate('');
    }
    if (endDate) {
      setEndDate('');
    }
  }, [startDate, endDate]);

  return (
    <Panel ref={ref} className="mr-3 mb-3">
      <Overlay
        target={target}
        rootClose={true}
        onHide={() => setIsOpen(false)}
        show={isOpen}
        placement="bottom"
      >
        <Popover id="date-range-popover" className="date-range-popover p-3">
          <Popover.Content>
            <div className="d-flex flex-column">
              <div className="d-flex w-100 justify-content-between align-items-center mb-2">
                <label className="m-0 pr-2" htmlFor={`start-date-${attribute}`}>{startLabel || 'Start'}</label>
                <input
                  type="date"
                  id={`start-date-${attribute}`}
                  name={`start-date-${attribute}`}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="d-flex w-100 justify-content-between align-items-center">
                <label className="m-0 pr-2" htmlFor={`end-date-${attribute}`}>{endLabel || 'End'}</label>
                <input
                  type="date"
                  id={`end-date-${attribute}`}
                  name={`end-date-${attribute}`}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </Popover.Content>
        </Popover>
      </Overlay>
      <button
        className="d-flex h-100 btn btn-outline-light border-border-grey button-shadow text-dark-btn-txt align-items-center date-trigger justify-content-between"
        onClick={(event) => {
          setIsOpen(!isOpen);
          setTarget(event.target as HTMLElement);
        }}
      >
        <span className="d-flex">
          {!!DisplayComponent && DisplayComponent}
          <span className="d-flex font-weight-bold">
            {`${startDate || 'Start'} - ${endDate || 'End'}`}
          </span>
        </span>
        {!!(startDate || endDate) && (
          <a
            href="#"
            aria-label="Clear date."
            className="clear"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              reset();
            }}
          >
            <svg
              height="20"
              width="20"
              viewBox="0 0 20 20"
              aria-hidden="true"
              focusable="false"
              className="clear"
            >
              <path
                d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"
              />
            </svg>
          </a>
        )}
      </button>
    </Panel>
  );
};

export default connectRange(DateRange);
